import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useHistory, useParams, useLocation  } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { secureGet, securePost } from '../../config/api';
import { formatDateTime, formatAmount, formatDate } from '../../utils/format';
import Input from '../../components/Input';
import Breadcrumbs from '../../components/Breadcrumbs';
import LoadingLogo from '../../components/LoadingLogo';
import SnackbarNotification from '../../components/SnackbarNotification';
import { EventMessage } from './EventMessage';
import MarkPaidPopup from '../../components/MarkPaidPopup';
import UserContext from '../../Shared/UserContext';
import AttachPopup from './AttachPopup';
import DeletePopup from "../../components/DeletePopup";
import ImagePopupViewer from "../../components/ImagePopupViewer";

import EditIcon from '../../icons/editB.svg';
import noImage from "../../icons/noImageIcon.svg";
import DeleteIcon from '../../icons/trashR.svg';
import UnpaidIcon from '../../icons/danger.svg';
import checkIcon from "../../icons/check.svg";
import plusIcon from '../../icons/plusP.svg';

const STATUS_COLORS = {
    CREATED: "#0283F4",
    UNDER_APPROVAL: "#FEA26F",
    VALIDATED: "#97DFFC",
    DELIVERED: "#06D6A0",
    DECLINED: "#EF466F",
    CANCELLED: "#A8ADC3",
};

const PaymentsDetails = () => {
    const history = useHistory();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('general_infos');
    const { t } = useTranslation();
    const { id } = useParams();
    const [payment, setPayment] = useState(null);
    const [, setLoading] = useState(true);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [attachPopupOpen, setAttachPopupOpen] = useState(false);

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };
    const { state: { role } } = useContext(UserContext);

    const handleAttachSuccess = () => {
        fetchPayment();
        showSnackbar(t(`confirmation.paymentAttach`));
    };

    const abortController = useMemo(() => new AbortController(), []);
    const { signal } = abortController;

    const fetchPayment = useCallback(async () => {
        setLoading(true);
        try {
            const response = await secureGet(`/payments/${id}`, showSnackbar, { signal });
            const fetchedPayment = response.data;
            setPayment(fetchedPayment);
            console.log(fetchedPayment);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Failed to fetch payment:", error);
            }
        } finally {
            setLoading(false);
        }
    }, [id, signal]);

    useEffect(() => {
        fetchPayment();

        return () => {
            abortController.abort();
        };
    }, [id, abortController, fetchPayment]);

    const handleModifyClick = () => {
        history.push({
            pathname: `/payments/details/modify/${payment.id}`,
            state: { filters: location.state?.filters || {}, page: location.state?.page || 0, size: location.state?.size || 30 },
        });
    };

    const detachBLConfirm = (bl) => {
        setItemToDelete(bl);
        setDeletePopupVisible(true);
    }

    const detachBL = async () => {
        const payload = {
            paymentId: payment.id,
            attachedAmount: itemToDelete.attachedAmount,
            deliveryNoteId: itemToDelete.deliveryNoteId,
        };
        try {
            const response = await securePost(`payments/detachCart`, payload, showSnackbar);
            setDeletePopupVisible(false);
            setItemToDelete(null);
            fetchPayment();

            showSnackbar(t(`confirmation.paymentDetach`));
        } catch (error) {
            console.error("Failed to detach BLs:", error);
        }
    }

    const handleMarkPaid = async (reason) => {
        const payload = {
            id: payment.id,
            reason: reason,
        }

        try {
            const response = await securePost("/payments/unmarkImpaye", payload, showSnackbar);
            setPopupOpen(false);
            fetchPayment();
            showSnackbar(t(`confirmation.markPaid`));
        } catch (error) {
            console.error("Failed to mark as unpaid:", error);
        }
    }


    const handleMarkUnpaid = async () => {
        try {
            const response = await securePost(`/payments/markImpaye/${payment.id}`, {}, showSnackbar);
            fetchPayment();
            showSnackbar(t(`confirmation.markUnpaid`));

        } catch (error) {
            console.error("Failed to mark as unpaid:", error);
        }
    };

    const handleUpdateStatus = async () => {
        try {
            const response = await securePost(`/payments/updateValidated`, { paymentId: payment.id }, showSnackbar);
            fetchPayment();
            showSnackbar(t(`confirmation.paymentValidation`));
        }
        catch (error) {
            console.error("Failed to update payment status:", error);
        }
    }

    const renderActionButtons = () => {
        return (
            <>
                {role === 'ROLE_ADMIN' && payment.status === "UNDER_APPROVAL" && (
                    <button
                        className="flex items-center justify-center bg-[#0AD69F] text-[#FFFFFF] font-bold text-[14px] px-6 py-3 rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
                        onClick={() => handleUpdateStatus("VALIDATED")}
                    >
                        <img src={checkIcon} alt="" className="w-5 h-5" />
                        {t(`actions.validate`)}
                    </button>
                )}
                <button
                    className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
                    onClick={handleModifyClick}
                >
                    <img src={EditIcon} alt="modify" className="w-5 h-5" />
                    {t('actions.modify')}
                </button>
            </>
        );
    };

    const handleMarkUnpaidClick = () => {
        setPopupOpen(true);
    };

    const handleCancelGenerate = () => {
        setPopupOpen(false);
    };

    if (!payment) {
        return <LoadingLogo />;
    }


    return (
        <div>
            {/* Payment Details */}
            <Helmet>
                <title>GrayFox - {t("titles.payment_details")}</title>
            </Helmet>
            <div className="flex flex-col mb-[20px]">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        <Breadcrumbs />
                        <h1 className="text-[32px] font-semibold">{t(`titles.payment_details`)}</h1>
                    </div>
                </div>
                <div className="flex items-center space-x-2 ">
                    <p className="text-[24px] font-semibold text-primary">
                        {payment.paymentNumber}
                    </p>
                </div>
            </div>



            <div className="flex gap-4">

                {/* LEFT */}
                <div className="w-[75%]">
                        <div className="flex gap-4 mt-6">
                            <div className="w-full">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                    <Input label={t(`paymentDetails.client`)} value={payment.grayClient?.companyName ?? payment.client?.name ?? ''} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.type`)} value={payment.type?.name ?? ''} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.bank`)} value={payment.bank?.name ?? ''} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.number`)} value={payment.paymentNumber ?? payment.reference ?? ''} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.amount`)} name="amount" value={payment.amount ?? ''} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.paymentDate`)} value={payment.paymentDate ? formatDate(payment.paymentDate) : ''} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.date_echeance`)} value={(payment.dueDate ?? payment.echeance) ? formatDate(payment.dueDate ?? payment.echeance) : ''} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.invoiceNumber`)} value={payment.invoiceNumber ?? ''} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.comment`)} value={payment.note ?? ''} onChange={() => { }} disabled={true} />
                                </div>
                            </div>
                        </div>
                    

                </div>

                {/* image */}
                <div className="w-[25%] mt-4">
                    <div className="bg-white shadow rounded-[12px] p-[2px] relative">
                        <div className="bg-[#F5F6FB] rounded-[12px] overflow-hidden ">
                            <ImagePopupViewer src={payment.image} fallbackSrc={noImage} />
                        </div>
                    </div>
                </div>
            </div>
            {payment && (
                <AttachPopup
                    open={attachPopupOpen}
                    onClose={() => setAttachPopupOpen(false)}
                    payment={payment}
                    clientId={payment.grayClient?.id ?? payment.client?.id}
                    onAttachSuccess={handleAttachSuccess}
                />
            )}

            {/* Delete Popup */}
            {isDeletePopupVisible && (
                <DeletePopup
                    open={isDeletePopupVisible}
                    onCancel={() => setDeletePopupVisible(false)}
                    onConfirm={detachBL}
                />
            )}

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />

            <MarkPaidPopup
                open={isPopupOpen}
                onConfirm={(reason) => handleMarkPaid(reason)}
                onCancel={handleCancelGenerate}
            />
        </div>
    );
};

export default PaymentsDetails;
